// Shared plan metadata for the per-plan enrollment chatbot.
// Keys must stay in sync with the edge function (supabase/functions/plan-chat/index.ts)
// and the enrollment_leads.plan_key check in the database.

export type PlanKey = "basic" | "full" | "refusal" | "readiness_1" | "readiness_2" | "readiness_3";

export type PlanMeta = {
  key: PlanKey;
  name: string;
  priceInr: number;
  tag: string;
  starters: string[];
};

export const PLAN_META: Record<PlanKey, PlanMeta> = {
  basic: {
    key: "basic",
    name: "Basic Support",
    priceInr: 3000,
    tag: "For confident applicants who just need practice",
    starters: [
      "What's included in Basic Support?",
      "How are the 2 mocks structured?",
      "Will I get DS-160 review in this plan?",
      "Is this enough if it's my first interview?",
    ],
  },
  full: {
    key: "full",
    name: "Full Support",
    priceInr: 4500,
    tag: "For first-timers who want end-to-end guidance",
    starters: [
      "Why pick Full Support over Basic?",
      "How many mocks do I get?",
      "What does DS-160 guidance actually cover?",
      "How long before my interview should I start?",
    ],
  },
  refusal: {
    key: "refusal",
    name: "Refusal Support",
    priceInr: 7000,
    tag: "After a 214(b) refusal - full re-attempt prep",
    starters: [
      "How is Refusal Support different from Full?",
      "How do you find what went wrong last time?",
      "When can I re-apply after a 214(b)?",
      "Do you guarantee approval on re-attempt?",
    ],
  },
  readiness_1: {
    key: "readiness_1",
    name: "Visa Readiness - Single Scan",
    priceInr: 300,
    tag: "1 AI profile scan + academic PDF report",
    starters: [
      "What does the ₹300 AI scan actually check?",
      "How is my approval probability calculated?",
      "What's in the downloadable PDF?",
    ],
  },
  readiness_2: {
    key: "readiness_2",
    name: "Visa Readiness - Dual Scan",
    priceInr: 500,
    tag: "2 AI scans - re-scan after fixing red flags",
    starters: [
      "Why re-scan my profile?",
      "How is Dual Scan different from a single scan?",
      "Do I get 2 PDF reports?",
    ],
  },
  readiness_3: {
    key: "readiness_3",
    name: "Visa Readiness - Pro Bundle",
    priceInr: 1200,
    tag: "4 AI scans + 1 Specialist written audit",
    starters: [
      "What does the Specialist expert review cover?",
      "How many scans do I get in the Pro Bundle?",
      "How fast is the 24-48h expert audit?",
    ],
  },
};

// Tiny event bus so any component (e.g. pricing Enroll buttons) can ask the
// floating ChatBot to open in plan mode.
export const PLAN_CHAT_EVENT = "welivdit:open-plan-chat";

export function openPlanChat(planKey: PlanKey) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PLAN_CHAT_EVENT, { detail: { planKey } }));
}
